/**
 * Theme Mode Provider Component
 * 
 * Theme mode provider for managing light/dark theme with:
 * - Theme state management
 * - LocalStorage persistence
 * - System preference detection
 * - Theme toggle functionality
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The theme mode provider component
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeContext, lightTheme, darkTheme } from './ThemeContext.helpers.js';

export const ThemeModeProvider = ({ children }) => {
  const getInitialTheme = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('wms-theme-mode');
      return saved ? saved === 'dark' : false;
    }
    return false;
  };

  const [darkMode, setDarkMode] = useState(getInitialTheme);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('wms-theme-mode', darkMode ? 'dark' : 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        const saved = localStorage.getItem('wms-theme-mode');
        if (!saved) setDarkMode(e.matches);
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const toggleTheme = useCallback(() => setDarkMode(prev => !prev), []);
  const theme = useMemo(() => darkMode ? darkTheme : lightTheme, [darkMode]);

  const value = useMemo(() => ({ 
    darkMode, 
    toggleTheme, 
    theme 
  }), [darkMode, toggleTheme, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};


export default ThemeModeProvider;

